import React from 'react';

import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  HeaderText,
  BodyText,
  Button,
  Stack,
} from '../../../../ui';
import { CloseIcon, ProAvatarPlaceholderIcon } from '../../../../ui/Icons';

import analytics from '../../../../../modules/analytics';
import { useMount } from '../../../../../hooks';

type TipRowProps = {
  index: number
  text: string
  cta: string
  ctaCallback: () => void
  ctaTestID: string
  tracking: any
};

const TipRow: React.FC<TipRowProps> = ({
  index, text, cta, ctaCallback, ctaTestID, tracking,
}) => (
  <Inline>
    <Box paddingRight={16}>
      <Box
        borderRadius={12}
        alignItems="center"
        justifyContent="center"
        color={colors.blue}
        style={{
          width: 24,
          height: 24,
        }}
      >
        <BodyText color={colors.whiteWash}>
          {index}
        </BodyText>
      </Box>
    </Box>
    <Stack
      alignItems="baseline"
      flex={1}
      space={16}
    >
      <Box>
        <HeaderText level={3}>{text}</HeaderText>
      </Box>
      <Button
        appearance="text-light"
        size="small"
        onClick={ctaCallback}
        testID={ctaTestID}
        clickTracking={tracking}
      >
        {cta}
      </Button>
    </Stack>
  </Inline>
);

export type Props = {
  close: () => void
  onPromoteProfileCTAClick: () => void
  onAddIGBookButtonCTAClick: () => void
  onSendLinkToClientsCTAClick: () => void
  onEditServicesCTALink: () => void
};

const GetMoreClientsModal: React.FC<Props> = ({
  close,
  onPromoteProfileCTAClick,
  onAddIGBookButtonCTAClick,
  onSendLinkToClientsCTAClick,
  onEditServicesCTALink,
}) => {
  const headlineText = 'How can I get more clients\non StyleSeat?';

  useMount(() => {
    analytics.track('pro_getmoreclients_modal_view');
  });
  return (
    <Stack
      alignItems="center"
      minHeight="100%"
      color={colors.chill}
    >
      <Box
        flexDirection="row-reverse"
        style={{ alignSelf: 'flex-end' }}
        paddingTop={12}
        paddingRight={12}
      >
        <Button
          appearance="none"
          testID="x-close-button"
          onClick={close}
          clickTracking="pro_getmoreclients_modal_dismiss"
        >
          <CloseIcon />
        </Button>
      </Box>
      <Stack
        maxWidth={400}
        space={16}
        paddingTop={8}
      >
        <Box alignItems="center">
          <ProAvatarPlaceholderIcon
            width={48}
            height={48}
            fillColor={colors.chill}
            backgroundColor={colors.itsOff}
          />
        </Box>
        <Box>
          <HeaderText level={1} textAlign="center">
            {headlineText}
          </HeaderText>
        </Box>
      </Stack>
      <Stack
        paddingX={24}
        paddingTop={24}
        space={28}
      >
        <TipRow
          index={1}
          text="Get instant exposure to new clients"
          cta="Promote Profile"
          ctaCallback={onPromoteProfileCTAClick}
          ctaTestID="promote-profile"
          tracking={{ name: 'pro_getmoreclients_modal_promote_click' }}
        />
        <TipRow
          index={2}
          text="Get the Instagram Book button"
          cta="Add Book Button"
          ctaCallback={onAddIGBookButtonCTAClick}
          ctaTestID="add-ig-book-button"
          tracking={{ name: 'pro_getmoreclients_modal_bookbutton_click' }}
        />
        <TipRow
          index={3}
          text="Invite existing clients to leave a review to build your brand for new clients"
          cta="Send Link To Clients"
          ctaCallback={onSendLinkToClientsCTAClick}
          ctaTestID="send-link-to-client"
          tracking={{ name: 'pro_getmoreclients_modal_sendlink_click' }}
        />
        <TipRow
          index={4}
          text="Polish your service menu with detailed descriptions, and photos"
          cta="Edit Service Descriptions"
          ctaCallback={onEditServicesCTALink}
          ctaTestID="edit-service-descriptions"
          tracking={{ name: 'pro_getmoreclients_modal_editservices_click' }}
        />
        <Box>
          <Button
            appearance="link"
            onClick={close}
            testID="close-button"
            clickTracking="pro_getmoreclients_modal_dismiss"
          >
            Close
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
};

export default GetMoreClientsModal;
